import type React from 'react';

export type RecipeTone = 'red' | 'blue' | 'amber' | 'emerald' | 'slate';

/**
 * A "recipe" — a named, outcome-first composite view. One click assembles
 * a whole config (filters / lenses / chips / zoom / target sub-panel) to
 * answer a concern the user has ("Cost Waste", "Span Overload", "Public
 * Path to Data"), instead of making them wire those controls by hand.
 *
 * Domain-agnostic: the orchestration lives in the domain-provided `apply`
 * closure, which composes that domain's own focus/lens/chip primitives.
 * The shared layer owns the card aesthetic and the deep-link/URL plumbing.
 *
 * Modelled on the Blueprint/Compass `LIVE_REVIEW_VIEWS` catalogue, lifted
 * into shared infra so every domain can offer the same UX with its own,
 * use-case-relevant recipes and naming.
 */
export interface ReviewRecipe {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  tone: RecipeTone;
  /** Apply the recipe — set filters/lenses/chips/zoom/panel for this domain. */
  apply: () => void;
  /** Optional teardown when the recipe is deselected or another is chosen. */
  clear?: () => void;
  /**
   * Whether the recipe's effects are currently in force. Lets the URL
   * plumbing re-assert a deep-linked recipe after a store re-initialization
   * clobbers it, without re-running `apply` when nothing was lost.
   */
  isApplied?: () => boolean;
  /** Optional trailing accessory on the card (e.g. a "Live" badge). */
  accessory?: React.ReactNode;
}

/** A titled group of recipes for sectioned card lists. */
export interface ReviewRecipeGroup {
  heading?: string;
  recipes: readonly ReviewRecipe[];
}

export const RECIPE_TONE_TEXT: Record<RecipeTone, string> = {
  red: 'text-rose-700',
  blue: 'text-blue-700',
  amber: 'text-amber-700',
  emerald: 'text-emerald-700',
  slate: 'text-slate-700',
};

export const RECIPE_TONE_BAR: Record<RecipeTone, string> = {
  red: 'bg-rose-500',
  blue: 'bg-blue-500',
  amber: 'bg-amber-500',
  emerald: 'bg-emerald-500',
  slate: 'bg-slate-500',
};

export const findRecipeById = (
  recipes: readonly ReviewRecipe[],
  id: string | null | undefined,
): ReviewRecipe | null => {
  if (!id) return null;
  return recipes.find((r) => r.id === id) ?? null;
};
